import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../config/routes';
import { useAppSelector } from '../redux/store';

interface Props {
  children: ReactNode;
  /**
   * Optional permission gate — if set, user must have a matching {module, action}
   * permission (or the same module's `admin` action) to access the route.
   * Mirror of Versility-React's PrivateRoutes permission engine.
   */
  permission?: { module: string; action: string };
}

const PrivateRoutes = ({ children, permission }: Props) => {
  const { user, status } = useAppSelector((s) => s.auth);
  const location = useLocation();

  if (status === 'idle' || status === 'loading') {
    return <div className="p-8 text-sm text-slate-500">Loading…</div>;
  }

  if (status !== 'authenticated' || !user) {
    return <Navigate to={ROUTES.LOGIN} replace state={{ from: location }} />;
  }

  if (permission) {
    const ok = user.permissions.some(
      (p) =>
        p.module === permission.module && (p.action === permission.action || p.action === 'admin'),
    );
    if (!ok) {
      return <div className="p-8 text-sm text-red-700">Forbidden — missing permission.</div>;
    }
  }

  return <>{children}</>;
};

export default PrivateRoutes;
